import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import {
  Users, Inbox, CalendarDays, MapPin, Download, LogOut, Search, Trash2, Phone, Mail, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['New', 'Contacted', 'Scheduled', 'Closed'];
const STATUS_STYLES = {
  New: 'bg-[#eaf4dd] text-[#3a6b12]',
  Contacted: 'bg-blue-100 text-blue-700',
  Scheduled: 'bg-amber-100 text-amber-700',
  Closed: 'bg-gray-200 text-gray-600',
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl p-5 border border-[#eef2e8] flex items-center gap-4">
    <div className="h-12 w-12 rounded-xl bg-[#eaf4dd] flex items-center justify-center">
      <Icon className="h-6 w-6 text-brand" />
    </div>
    <div>
      <div className="text-2xl font-extrabold text-[#13251b] font-display">{value}</div>
      <div className="text-sm text-[#5b6a60]">{label}</div>
    </div>
  </div>
);

const AdminDashboard = ({ token, onLogout }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      toast.error('Session expired — please log in again.');
      onLogout();
    }
  }, [onLogout]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, auth);
      setStats(res.data);
    } catch (err) { handleAuthError(err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'All') params.status = statusFilter;
      if (q) params.q = q;
      const res = await axios.get(`${API}/admin/leads`, { ...auth, params });
      setLeads(res.data);
    } catch (err) { handleAuthError(err); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, q]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(loadLeads, 250);
    return () => clearTimeout(t);
  }, [loadLeads]);

  const saveLead = async (updates) => {
    if (!active) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/leads/${active.id}`, updates, auth);
      setActive(res.data);
      setLeads((ls) => ls.map((l) => (l.id === res.data.id ? res.data : l)));
      loadStats();
      toast.success('Lead updated');
    } catch (err) { handleAuthError(err); toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, auth);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setActive(null);
      loadStats();
      toast.success('Lead deleted');
    } catch (err) { handleAuthError(err); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, { ...auth, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'leads.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { handleAuthError(err); }
  };

  const fmt = (d) => new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="min-h-screen bg-[#f4f7f0]">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/360moldlogo.png" alt="logo" className="h-10" />
            <span className="font-display font-bold text-[#13251b] hidden sm:block">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={exportCsv} variant="outline" className="rounded-full border-[#cbdcbb] text-[#13251b] gap-2">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">Export CSV</span>
            </Button>
            <Button onClick={onLogout} className="rounded-full bg-[#13251b] hover:bg-[#20372a] text-white gap-2">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total leads" value={stats?.total ?? '—'} />
          <StatCard icon={Inbox} label="New / unactioned" value={stats?.new ?? '—'} />
          <StatCard icon={CalendarDays} label="This week" value={stats?.week ?? '—'} />
          <StatCard icon={MapPin} label="Top city" value={stats?.top_cities?.[0]?.city ?? '—'} />
        </div>

        <div className="bg-white rounded-2xl border border-[#eef2e8] overflow-hidden">
          <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between border-b">
            <div className="relative sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, city..." className="pl-9" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['All', ...STATUSES].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    statusFilter === s ? 'bg-brand text-white' : 'bg-[#f4f7f0] text-[#5b6a60] hover:bg-[#eaf4dd]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : leads.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">No leads found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#fafcf7] text-[#5b6a60] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold hidden md:table-cell">Contact</th>
                    <th className="px-4 py-3 font-semibold hidden lg:table-cell">City</th>
                    <th className="px-4 py-3 font-semibold hidden lg:table-cell">Service</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold hidden sm:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} onClick={() => setActive(l)} className="border-t hover:bg-[#fafcf7] cursor-pointer">
                      <td className="px-4 py-3 font-medium text-[#13251b]">{l.name}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-[#5b6a60]">{l.phone}<br />{l.email}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-[#5b6a60]">{l.city || '—'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-[#5b6a60]">{l.service || '—'}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATUS_STYLES[l.status] || ''} border-0 font-medium`}>{l.status}</Badge>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-[#5b6a60]">{fmt(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{active.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-4 text-sm">
                  <a href={`tel:${active.phone}`} className="flex items-center gap-2 text-brand font-medium"><Phone className="h-4 w-4" /> {active.phone}</a>
                  <a href={`mailto:${active.email}`} className="flex items-center gap-2 text-brand font-medium"><Mail className="h-4 w-4" /> {active.email}</a>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-muted-foreground">City</div><div className="font-medium text-[#13251b]">{active.city || '—'}</div></div>
                  <div><div className="text-muted-foreground">Service</div><div className="font-medium text-[#13251b]">{active.service || '—'}</div></div>
                  <div><div className="text-muted-foreground">Source page</div><div className="font-medium text-[#13251b]">{active.source || '—'}</div></div>
                  <div><div className="text-muted-foreground">Submitted</div><div className="font-medium text-[#13251b]">{fmt(active.created_at)}</div></div>
                </div>
                {active.message && (
                  <div className="text-sm">
                    <div className="text-muted-foreground">Message</div>
                    <p className="mt-1 bg-[#fafcf7] rounded-lg p-3 text-[#3d4c43]">{active.message}</p>
                  </div>
                )}
                <div>
                  <div className="text-sm text-muted-foreground mb-1.5">Status</div>
                  <Select value={active.status} onValueChange={(v) => saveLead({ status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1.5">Internal notes</div>
                  <Textarea
                    defaultValue={active.notes}
                    onBlur={(e) => { if (e.target.value !== active.notes) saveLead({ notes: e.target.value }); }}
                    rows={3}
                    placeholder="Add a private note..."
                  />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" onClick={() => deleteLead(active.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-2">
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                  {saving && <span className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Saving...</span>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
